'use client';

import { useEffect } from 'react';
import { attemptChunkReload, clearChunkReloadGuard, isChunkLoadError } from '@/lib/chunkError';

// Safety net for ChunkLoadErrors that skip React error boundaries — thrown in
// router prefetch or async handlers, surfacing as window error/rejection events.
// Common on paid mobile roundups: idle 4G tab, lazy chunk fetch fails (aged
// deployment or dropped request), hard-reload once to recover.
// Renders nothing. Mount high in each root layout.
export function ChunkErrorReloader() {
  useEffect(() => {
    // Mounted, so any prior reload worked — reset guard for future failures.
    clearChunkReloadGuard();

    function handleError(event: ErrorEvent) {
      if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
        attemptChunkReload(event.error ?? event.message);
      }
    }

    function handleRejection(event: PromiseRejectionEvent) {
      if (isChunkLoadError(event.reason)) {
        attemptChunkReload(event.reason);
      }
    }

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
